/**
 * Les calculs PURS du dossier de consultation (soins, cloture, choix d'un
 * produit) : aucun React, aucun SDK, testes par `poste.test.mjs`. Les temps et
 * les textes vivent dans `temps.ts`.
 */

/** Le total indicatif : les lignes sans prix connu ne comptent pas. */
export function totalDesLignes(lignes: readonly { quantite: number; prix: number | null }[]): number {
  return lignes.reduce((somme, ligne) => {
    if (ligne.prix === null) return somme;
    const quantite = Number.isFinite(ligne.quantite) && ligne.quantite > 0 ? ligne.quantite : 1;
    return somme + ligne.prix * quantite;
  }, 0);
}

/** Ce qui manque pour cloturer : bloquant (aucune ligne) ou simple avertissement. */
export function manquesPourCloturer(entree: { lignes: number; compteRendu: Record<string, unknown> }): {
  bloquants: string[];
  avertissements: string[];
} {
  const bloquants = entree.lignes === 0 ? ['Aucun soin ni produit indiqué.'] : [];
  const vides = (
    [
      ['anamnesis', 'Anamnèse'],
      ['exam', 'Examen clinique'],
      ['diagnosis', 'Diagnostic'],
    ] as const
  )
    .filter(([cle]) => {
      const valeur = entree.compteRendu[cle];
      return typeof valeur !== 'string' || valeur.trim() === '';
    })
    .map(([, libelle]) => libelle);
  const avertissements = vides.length === 0 ? [] : [`Compte rendu incomplet : ${vides.join(', ')}.`];
  return { bloquants, avertissements };
}

/**
 * LES GROUPES DU CHOIX DE PRODUIT, dans l'ordre des portes du Catalogue. Les
 * categories sont les valeurs du jeu `products/article` (app Catalogue) ; un
 * produit sans categorie connue tombe dans « Autres ».
 */
const GROUPES_DU_CATALOGUE: ReadonlyArray<{ titre: string; categories: readonly string[] }> = [
  { titre: 'Actes', categories: ['Acte'] },
  { titre: 'Médicaments', categories: ['Vaccin', 'Médicament'] },
  { titre: 'Consommables', categories: ['Consommable'] },
  { titre: 'Équipements', categories: ['Équipement'] },
];

export function groupesDuCatalogue<P extends { customFields?: Record<string, unknown> | undefined }>(
  produits: readonly P[],
): Array<{ titre: string; produits: P[] }> {
  const categorie = (p: P): string => String(p.customFields?.['category'] ?? '');
  const connues = GROUPES_DU_CATALOGUE.flatMap((g) => g.categories);
  const groupes = GROUPES_DU_CATALOGUE.map((g) => ({ titre: g.titre, produits: produits.filter((p) => g.categories.includes(categorie(p))) }));
  groupes.push({ titre: 'Autres', produits: produits.filter((p) => !connues.includes(categorie(p))) });
  return groupes.filter((g) => g.produits.length > 0);
}

/**
 * LES LIGNES D'UN PROTOCOLE : un protocole est un produit `protocole` du
 * Catalogue, et chacune de ses lignes est une fiche `compositions` de type
 * `ligne` dont le natif `productId` est l'id du protocole, avec `articleId`
 * (le produit) et `quantite` dans son sac. Une ligne sans produit ne donne
 * rien : elle ne se facture pas. Le rang des lignes est celui du tableau recu.
 */
export function lignesDuProtocole(
  lignes: readonly { customFields?: Record<string, unknown> | undefined }[],
): Array<{ productId: string; quantity: number }> {
  return lignes
    .map((ligne) => {
      const sac = ligne.customFields ?? {};
      const quantite = Number(sac['quantite'] ?? 1);
      return {
        productId: typeof sac['articleId'] === 'string' ? sac['articleId'] : '',
        quantity: Number.isFinite(quantite) && quantite > 0 ? quantite : 1,
      };
    })
    .filter((ligne) => ligne.productId !== '');
}

/**
 * LES LIGNES DE LA CONSULTATION, rangees : d'abord celles ajoutees une a une,
 * puis un groupe par protocole ajoute, sous son nom, dans l'ordre d'arrivee.
 * Dans un groupe, les lignes suivent l'ordre du protocole (`rangDansLeProtocole`,
 * le rang du produit dans sa composition) : creees d'un coup, elles reviennent
 * de la base dans un ordre qui ne dit rien.
 */
export function groupesDeLignes<L extends { customFields?: Record<string, unknown> | undefined }>(
  lignes: readonly L[],
  rangDansLeProtocole: (protocolId: string, productId: string) => number = () => 0,
): Array<{ protocolId: string | null; lignes: L[] }> {
  const protocoleDe = (l: L): string | null => {
    const id = l.customFields?.['protocolId'];
    return typeof id === 'string' && id !== '' ? id : null;
  };
  const groupes: Array<{ protocolId: string | null; lignes: L[] }> = [{ protocolId: null, lignes: lignes.filter((l) => protocoleDe(l) === null) }];
  for (const ligne of lignes) {
    const id = protocoleDe(ligne);
    if (id === null) continue;
    const groupe = groupes.find((g) => g.protocolId === id);
    if (groupe === undefined) groupes.push({ protocolId: id, lignes: [ligne] });
    else groupe.lignes.push(ligne);
  }
  for (const groupe of groupes) {
    const protocolId = groupe.protocolId;
    if (protocolId === null) continue;
    const rang = (l: L): number => {
      const productId = l.customFields?.['productId'];
      const r = rangDansLeProtocole(protocolId, typeof productId === 'string' ? productId : '');
      return r < 0 ? Number.MAX_SAFE_INTEGER : r;
    };
    groupe.lignes.sort((a, b) => rang(a) - rang(b));
  }
  return groupes.filter((g) => g.lignes.length > 0);
}

/**
 * LE CHOIX « AJOUTER » DE MON POSTE : produits et protocoles dans UNE liste qui
 * se cherche (`OptionList` du socle), rangee sous les titres des portes du
 * Catalogue, puis « Protocoles ». La recherche lit le nom, le detail et des
 * mots caches : la categorie, le titre du groupe, le code. Taper « vaccin » ou
 * « consommable » garde donc une categorie entiere, et un mot se trouve
 * n'importe ou dans le nom (Fuga ne cherche qu'en debut de mot).
 * Valeur : `produit:<id>` ou `protocole:<id>`, lue par `lireChoix`.
 *
 * LES ACCENTS : la recherche du socle compare les lettres telles quelles, et
 * « medi » ne trouvait pas « Médicaments ». Chaque entree porte donc aussi ses
 * mots SANS ACCENTS dans `motsCles` : taper sans accent trouve tout. Taper un
 * accent que le nom n'a pas reste sans reponse : c'est au socle de comparer
 * sans accents (tache du socle).
 */
export interface EntreeDuChoix {
  value: string;
  label: string;
  detail?: string;
  motsCles: string;
  groupe: string;
}

/** Le texte sans ses accents (« Médicament » donne « Medicament »). */
export function sansAccents(texte: string): string {
  return texte.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

/** Les mots caches d'une entree : les mots donnes, puis tous les textes sans accents. */
function motsAvecEtSansAccents(textes: readonly string[]): string {
  const mots = textes.filter((t) => t !== '').join(' ');
  return `${mots} ${sansAccents(mots)}`;
}

export function entreesDuChoix<
  P extends { id: string; name: string; sku?: string | null | undefined; unit?: string | undefined; customFields?: Record<string, unknown> | undefined },
>(
  produits: readonly P[],
  protocoles: ReadonlyArray<{ id: string; name: string; lignes: number; prix: number | null }>,
  enTexte: (prix: number) => string,
): EntreeDuChoix[] {
  const entrees: EntreeDuChoix[] = [];
  for (const groupe of groupesDuCatalogue(produits)) {
    for (const p of groupe.produits) {
      const prix = p.customFields?.['priceInclTax'];
      entrees.push({
        value: `produit:${p.id}`,
        label: p.name,
        /* Le prix PAR UNITE du catalogue : « 35,00 € / dose ». */
        ...(typeof prix === 'number' ? { detail: p.unit === undefined || p.unit === '' ? enTexte(prix) : `${enTexte(prix)} / ${p.unit}` } : {}),
        motsCles: motsAvecEtSansAccents([p.name, String(p.customFields?.['category'] ?? ''), groupe.titre, p.sku ?? '']),
        groupe: groupe.titre,
      });
    }
  }
  for (const pr of protocoles) {
    entrees.push({
      value: `protocole:${pr.id}`,
      label: pr.name,
      detail: [`${pr.lignes} ${pr.lignes > 1 ? 'lignes' : 'ligne'}`, pr.prix === null ? '' : enTexte(pr.prix)].filter((x) => x !== '').join(' · '),
      motsCles: motsAvecEtSansAccents([pr.name, 'Protocole Protocoles']),
      groupe: 'Protocoles',
    });
  }
  return entrees;
}

/** Ce qu'une valeur du choix designe, ou null si elle ne dit rien. */
export function lireChoix(valeur: string | null): { sorte: 'produit' | 'protocole'; id: string } | null {
  const trouve = /^(produit|protocole):(.+)$/.exec(valeur ?? '');
  return trouve === null ? null : { sorte: trouve[1] as 'produit' | 'protocole', id: trouve[2] ?? '' };
}
